import {
  Button,
  Description,
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  Field,
  Fieldset,
  Input,
  Label,
} from "@headlessui/react";
import { Message } from "_components";
import { API_ENDPOINT, cx } from "_lib/utils";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

const LeadForm = ({
  open,
  onClose,
  destination = "Singapore",
}: {
  open: boolean;
  onClose: Function;
  destination: string;
}) => {
  const [isOpen, setIsOpen] = useState(open);
  const [isMsgOpen, setIsMsgOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullname: "",
    mobile: "",
    email: "",
    destination: destination,
    travelDate: "",
    pax: 2,
  });

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  const handleClose = () => {
    setIsOpen(false);
    onClose();
  };

  const handleChange = (event: { target: { name: any; value: any } }) => {
    let { name, value } = event.target;
    value = name === "pax" && value ? parseInt(value) : value;
    setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));
  };

  const handleSubmit = (event: any) => {
    setIsLoading(true);
    event.preventDefault();
    axios.post(`${API_ENDPOINT}/items/leads`, formData).then((_res) => {
      handleClose();
      setIsLoading(false);
      setIsMsgOpen(true);
      setFormData({
        fullname: "",
        mobile: "",
        email: "",
        destination: destination,
        travelDate: "",
        pax: 2,
      });
      setTimeout(() => {
        setIsMsgOpen(false);
      }, 2000);
    });
  };

  return (
    <>
      <Dialog open={isOpen} onClose={handleClose} className="relative z-30">
        {/* The backdrop, rendered as a fixed sibling to the panel container */}
        <DialogBackdrop className="fixed inset-0 bg-black/50" />

        {/* Full-screen container to center the panel */}
        <div className="fixed inset-0 flex w-screen items-center justify-center max-h-fit overflow-scroll p-4">
          {/* The actual dialog panel  */}
          <DialogPanel className="max-w-lg space-y-8 bg-white rounded-lg p-8">
            <div>
              <DialogTitle className="font-bold text-lg">
                Submit your query
              </DialogTitle>
              <Description className={"text-sm text-slate-600"}>
                Our team will get back to you within 24 hours to discuss about
                your tour.
              </Description>
            </div>
            <form className="w-full" onSubmit={handleSubmit}>
              <Fieldset className="space-y-6">
                {/* <Legend className="text-base/7 font-semibold">
                Tour details
              </Legend> */}
                <Field>
                  <Label className="text-sm/6 font-medium">Full Name</Label>
                  <Input
                    required
                    name="fullname"
                    maxLength={64}
                    value={formData.fullname}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    className="mt-1 block w-full border rounded-lg py-1.5 px-3 text-sm/6"
                  />
                </Field>

                <Field>
                  <Label className="text-sm/6 font-medium">Mobile Number</Label>
                  <Input
                    pattern="^\d{10}$"
                    required
                    name="mobile"
                    max={10}
                    maxLength={10}
                    min={10}
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="10 digit mobile number"
                    className="mt-1 block w-full border rounded-lg py-1.5 px-3 text-sm/6"
                  />
                </Field>

                <Field>
                  <Label className="text-sm/6 font-medium">Email</Label>
                  <Input
                    required
                    name="email"
                    type="email"
                    maxLength={64}
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    className="mt-1 block w-full border rounded-lg py-1.5 px-3 text-sm/6"
                  />
                </Field>
                <Field>
                  <Label className="text-sm/6 font-medium">Destination</Label>
                  <Input
                    required
                    name="destination"
                    type="text"
                    maxLength={32}
                    value={formData.destination}
                    onChange={handleChange}
                    placeholder="Enter your travel destination"
                    className="mt-1 block w-full border rounded-lg py-1.5 px-3 text-sm/6"
                  />
                </Field>

                <Field>
                  <Label className="text-sm/6 font-medium">
                    Planned Travel Date
                  </Label>
                  <Input
                    name="travelDate"
                    value={formData.travelDate}
                    onChange={handleChange}
                    type="date"
                    min={new Date().toISOString().split("T")[0]}
                    required
                    placeholder="Select your travel date"
                    className="mt-1 block w-full border rounded-lg py-1.5 px-3 text-sm/6"
                  />
                </Field>
                <Field>
                  <Label className="text-sm/6 font-medium">
                    Total Passengers
                  </Label>
                  <Input
                    name="pax"
                    value={formData.pax}
                    onChange={handleChange}
                    type="number"
                    min={1}
                    max={50}
                    required
                    placeholder="Enter total number of passengers"
                    className="mt-1 block w-full border rounded-lg py-1.5 px-3 text-sm/6"
                  />
                </Field>
              </Fieldset>
              <div className="flex justify-end gap-5 mt-8">
                {!isLoading && <Button onClick={handleClose}>Cancel</Button>}
                <Button
                  type="submit"
                  disabled={isLoading}
                  className={cx(
                    "font-bold text-white rounded-lg px-4 py-2",
                    isLoading ? "bg-gray-400 px-8" : "bg-primary"
                  )}
                >
                  {isLoading ? <Loader2 className="animate-spin" /> : "Submit"}
                </Button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </Dialog>
      <Message
        text="Your query has been submitted successfully!"
        open={isMsgOpen}
        onClose={() => setIsMsgOpen(false)}
      />
    </>
  );
};

export default LeadForm;
